/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { getPresetRange } from './presets';
import { DateRange, Preset } from './types';

export const setPreset = (
  preset: string,
  presetArray: Preset[],
  setRange: (range: DateRange) => void,
  rangeCompare: DateRange | undefined,
  setRangeCompare: (rangeCompare: DateRange | undefined) => void
): void => {
  // Skip processing if preset is allTime
  if (preset === 'allTime') {
    return;
  }

  const range = getPresetRange(preset, presetArray);

  setRange(range);
  if (rangeCompare && range.from && range.to) {
    const compareRange = {
      from: new Date(
        range.from.getFullYear() - 1,
        range.from.getMonth(),
        range.from.getDate()
      ),
      to: range.to
        ? new Date(
            range.to.getFullYear() - 1,
            range.to.getMonth(),
            range.to.getDate()
          )
        : undefined,
    };
    setRangeCompare(compareRange);
  }
};

// find a suitable preset based on the current range
export const checkPreset = (
  PRESETS: Preset[],
  range: DateRange,
  setSelectedPreset: (preset: string | undefined) => void,
  isComingFromArchitecturePage: boolean = false
): void => {
  // Handle the case where range has undefined dates (All time)
  if (!range.from || !range.to) {
    if (isComingFromArchitecturePage) {
      setSelectedPreset('tillLastDiscovered');
    } else {
      setSelectedPreset('allTime');
    }
    return;
  }

  for (const preset of PRESETS) {
    // Skip checking allTime preset
    if (preset.name === 'allTime') {
      continue;
    }

    const presetRange = getPresetRange(preset.name, PRESETS);

    // Skip if preset range has undefined dates
    if (!presetRange.from || !presetRange.to) {
      continue;
    }

    if (preset.name === 'last5mins' || preset.name === 'lastHour') {
      // calculate expected duration of the preset in milliseconds
      const duration = Math.abs(
        presetRange.from.getTime() - presetRange.to.getTime()
      );
      // allow 1 min buffer from current time
      const timeTolerance = 60 * 1000;
      // check if range matches preset duration and ends near current time
      if (
        Math.abs(range.from.getTime() - range.to.getTime()) === duration &&
        Math.abs(range.to.getTime() - presetRange.to.getTime()) < timeTolerance
      ) {
        setSelectedPreset(preset.name);
        return;
      }
    } else {
      const normalizedRangeFrom = new Date(range.from);
      normalizedRangeFrom.setHours(0, 0, 0, 0);

      const normalizedPresetFrom = new Date(presetRange.from);
      normalizedPresetFrom.setHours(0, 0, 0, 0);

      const normalizedRangeTo = new Date(range.to);
      normalizedRangeTo.setHours(0, 0, 0, 0);

      const normalizedPresetTo = new Date(presetRange.to);
      normalizedPresetTo.setHours(0, 0, 0, 0);

      if (
        // check that both calendar dates and start time match the preset
        // Note: end time is not checked because presets dynamically end at 'now', making exact matching unreliable.
        normalizedRangeFrom.getTime() === normalizedPresetFrom.getTime() &&
        normalizedRangeTo.getTime() === normalizedPresetTo.getTime() &&
        range.from.getHours() === presetRange.from.getHours() &&
        range.from.getMinutes() === presetRange.from.getMinutes()
      ) {
        setSelectedPreset(preset.name);
        return;
      }
    }
  }

  setSelectedPreset(undefined);
};
